import { useEffect, useRef, type RefObject } from "react";
import * as echarts from "echarts/core";
import type { EChartsOption } from "echarts";

const useEChart = (option: EChartsOption): RefObject<HTMLDivElement | null> => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;

    // jsdom (unit tests) has no ResizeObserver — fall back to a window
    // resize listener rather than throwing on mount in that environment.
    const resize = () => chart.resize();
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", resize);
    } else {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", resize);
      }
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    // notMerge: true — chart types here have structurally different options
    // (e.g. dual-axis bar+line vs. a pie), so merging the previous option in
    // would leave stale series/axes behind rather than replacing them.
    chartRef.current?.setOption(option, true);
  }, [option]);

  return containerRef;
};

export default useEChart;
